import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import React from 'react';
import { SvgIcon } from '../SvgIcon/SvgIcon';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { Theme } from '~/Utils/Theme';
import styles from './Select.module.css';
import type { SelectSize } from './SelectSize';
import type { SelectVariant } from './SelectVariant';

type NativeSelectAttributes = Pick<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    | 'required'
    | 'aria-selected'
    | 'aria-expanded'
    | 'aria-describedby'
    | 'aria-label'
    | 'tabIndex'
    | 'title'
    | 'id'
    | 'disabled'
    | 'onFocus'
    | 'onBlur'
>;

interface BaseSelectProps extends NativeSelectAttributes {
    options: Map<string, string>;
    onChange: (selection: string) => void;
    value?: string;
    defaultValue?: string;
    className?: string;
    variant?: SelectVariant;
    size?: SelectSize;
    theme?: Theme;
    placeholder?: string;
}

interface SelectWithLabel extends BaseSelectProps {
    label: string;
}

type SelectWithAria = Required<Pick<React.SelectHTMLAttributes<HTMLSelectElement>, 'aria-describedby'>> &
    BaseSelectProps;

export type SelectProps = SelectWithLabel | SelectWithAria;

export const Select: React.FC<SelectProps> = props => {
    const contextualTheme = useTheme();
    const {
        defaultValue,
        options,
        className,
        onChange,
        placeholder,
        variant = 'light',
        theme = contextualTheme,
        size = 'medium',
        ...restProps
    } = props;
    const hasLabel = 'label' in props && props.label;

    const { withRoundedCorners } = theme;
    const id = uniqueId('select');

    return (
        <div
            className={classNames(
                styles[`Variant--${variant}`],
                styles[`Variant--${size}`],
                withRoundedCorners && styles['Variant--rounded'],
                className
            )}
        >
            {hasLabel && (
                <label htmlFor={id} className={styles.Label}>
                    {props.label}
                </label>
            )}
            <div className={styles.Inner}>
                <select
                    id={id}
                    className={styles.Native}
                    defaultValue={defaultValue}
                    onChange={e => onChange(e.target.value)}
                    {...restProps}
                >
                    {placeholder && <option>{placeholder}</option>}
                    {Array.from(options).map(([value, optionLabel]) => (
                        <option key={value} value={value}>
                            {optionLabel}
                        </option>
                    ))}
                </select>
                <SvgIcon className={styles.Icon} iconName="arrow-triangle" />
            </div>
        </div>
    );
};
